import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Autocomplete from "@material-ui/lab/Autocomplete";
import parse from "autosuggest-highlight/parse";
import clsx from "clsx";
import { TextField } from "components/TextField";
import useText from "constants/text";
import { Address } from "generated/graphql";
import { useCurrentClient } from "hooks";
import throttle from "lodash/throttle";
import React from "react";

// @ts-ignore
const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  autoCompleteAdresse: {
    [theme.breakpoints.down("xs")]: {
      marginBottom: "37px",
    },
  },
  textfieldPhone: {
    [theme.breakpoints.down("xs")]: {
      margin: "0px 0px 6px 0px",
    },
  },
}));

export interface PlaceType {
  description: string;
  place_id?: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
}

export interface GeoPlaceProps {
  onChange?: (address: Address) => void;
  address?: Address;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  onSelect?: (event: React.SyntheticEvent<HTMLDivElement, Event>) => void;
  customLabel?: string;
  placeholder?: string;
  className?: string;
  locationIcon?: boolean;
}
export function GeoAutoCompletion({
  onChange,
  disabled = false,
  locationIcon=false,
  address = {
    address: "",
    place_id: "",
  },
  required = false,
  helperText = "",
  error = false,
  onSelect,
  customLabel,
  placeholder = "",
}: GeoPlaceProps) {
  const classes = useStyles();
  const [value, setValue] = React.useState<PlaceType | null>({
    description: address?.address,
    place_id: address?.place_id,
  });
  const { text } = useText();
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<PlaceType[]>([]);
  const { client } = useCurrentClient();

  React.useEffect(() => {
    if (address && address?.address?.length > 0) {
      setValue({ description: address?.address });
    }
  }, [address]);

  const fetch = React.useMemo(
    () =>
      throttle((request: { input: string }, callback: (results?: PlaceType[]) => void) => {
        (autocompleteService.current as any).getPlacePredictions(
          {
            ...request,
            componentRestrictions: {
              country: client?.countries?.length ? client?.countries.map((element: any) => element.code) : "sn",
            },
          },
          callback
        );
      }, 200),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "" || inputValue === null) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: PlaceType[]) => {
      if (active) {
        let newOptions = [] as PlaceType[];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      style={{
        backgroundColor: disabled ? "#ececec" : "#fff",
      }}
      className={clsx(classes.autoCompleteAdresse, classes.textfieldPhone)}
      id="google-map-demo"
      getOptionLabel={(option) => (typeof option === "string" ? option : option?.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      disabled={disabled}
      popupIcon={ locationIcon ? <LocationOnIcon /> :  <ArrowDropDownIcon />}
      onChange={(event: any, newValue: any) => {
        setOptions(newValue?.place_id?.length ? [newValue, ...options] : options);
        setValue(newValue);
        onChange({
          address: newValue?.description,
          place_id: newValue?.place_id,
        });
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onSelect={(eventValue) => onSelect}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          required={required}
          {...params}
          label={customLabel || text.all.address}
          variant="outlined"
          fullWidth
          onChange={(val) => {
            onChange({
              address: val.target.value,
            });
          }}
        />
      )}
      renderOption={(option) => {
        if (option.description === "" || option.place_id === "" || !option?.structured_formatting) return;
        const matches = option?.structured_formatting?.main_text_matched_substrings;
        if (!Array.isArray(matches)) return;
        const parts = parse(
          option?.structured_formatting?.main_text,
          matches?.map((match: any) => [match?.offset, match?.offset + match?.length])
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part: { highlight: any; text: React.ReactNode }, index: string | number) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}

export default GeoAutoCompletion;
